import { createSelector } from '@reduxjs/toolkit';
import {
    hasInstantConfirmationBadge,
    mapCancellationTypeByCancellation,
    mapContentByActivityWithContent,
    mapDetailByActivityWithContent,
    mapLogisticsByDetail,
    mapTicketsByDetail,
} from 'gql/service/graph/activityService';
import { CancellationType } from 'gql/autogen/CancellationType';
import { LogisticType } from 'gql/autogen/LogisticType';
import { TicketType } from 'gql/autogen/TicketType';
import { selectActivityFromDetailState } from 'component/page/detail/store/detail.selectors';
import { selectCmsFromBootstrap, selectBehaviorFromBootstrap, selectExperimentsFromBootstrap } from 'store/slice/bootstrap';
import getDurationText from 'core/utils/getDurationText';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';

// Order matters. These values are displayed in order of keys on the Details page.
export enum KeyInfoType {
    duration = 'duration',
    cancellation = 'cancellation',
    freeCancellation = 'freeCancellation',
    pickUp = 'pickUp',
    mobileTicket = 'mobileTicket',
    printTicket = 'printTicket',
    instantConfirmation = 'instantConfirmation',
}

export const mapStateToProps = createSelector(
    selectCmsFromBootstrap,
    selectActivityFromDetailState,
    selectBehaviorFromBootstrap,
    selectExperimentsFromBootstrap,
    (cms, activity, behavior, experiments) => {
        const activityContent = mapContentByActivityWithContent(activity);
        const detail = mapDetailByActivityWithContent(activity);
        const cancellationPolicy = mapCancellationTypeByCancellation(activity.activityRepresentativeInfo?.cancellationPolicy);
        const logistic = mapLogisticsByDetail(detail);
        const ticketType = mapTicketsByDetail(detail);
        const duration = activityContent.activity && activityContent.activity.duration ? getDurationText(activityContent.activity.duration, cms) : '';
        const isInstantConfirmationActivity = isOn(FeatureName.BadgesV2DetailsPage) ? hasInstantConfirmationBadge(activityContent) : false;
        const keyInfoProps: Record<keyof typeof KeyInfoType, string> = {
            cancellation: cancellationPolicy === CancellationType.NON_REFUNDABLE ? cms[126846] : '',
            freeCancellation: cancellationPolicy === CancellationType.FREE_CANCELLATION ? cms[145602] : '',
            pickUp: logistic.logisticType === LogisticType.PICKUP || logistic.logisticType === LogisticType.PICKUP_AND_MEETUP ? cms[130948] : '',
            mobileTicket: ticketType && ticketType.includes(TicketType.MOBILE) ? cms[130949] : '',
            printTicket: ticketType && ticketType.includes(TicketType.PRINT) ? cms[130950] : '',
            duration,
            instantConfirmation: isInstantConfirmationActivity ? cms[337019] : '',
        };
        if (behavior.common.hideFreeCancellation) {
            keyInfoProps.freeCancellation = '';
        }

        const keyInfoItems = Object.keys(KeyInfoType)
            .map((key) => {
                const keyInfo = key as KeyInfoType;
                return {
                    type: keyInfo,
                    text: keyInfoProps[keyInfo],
                };
            })
            .filter((item) => item.text);
        return keyInfoItems;
    },
);
